import {
    findFieldValueFromCompanyCompanyConfigurationFormField
} from "@/components/forms/customizations/plm/EntityEditFormService";
import Rpc from "@/Rpc";
import DecimalFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/DecimalFormFieldConfig";
import ActivityManager from "@/activity/ActivityManager";
import DecimalWithCurrency from "@/server/com/silkplm/rpc/dto/DecimalWithCurrency";
import BigDecimal from "@/server/BigDecimal";
import {cloneClassInstance, cloneEntity} from "@/utils/Objects";

const activityClass = () => require("@/components/FieldTranslationsActivity");


export function flagImgLinkForInput(formField) {
    return 'https://silkplm.com/wp-content/uploads/flags/' + detectInputTypingLanguage(formField) + '.webp'
}

export function detectInputTypingLanguage(formField) {
    return detectDBLanguage(formField);
}

export function detectDBLanguage(formField) {
    const defaultCustomerLanguageCode = SessionManager.defaultCustomerLanguageCode;
    return defaultCustomerLanguageCode;
}

export function isInterfaceTranslationEnabledOnField(formField) {
    if (!SessionManager.interfaceLanguages) {
        return false;
    }
    if (Object.keys(SessionManager.interfaceLanguages).length < 2) {
        return false;
    }
    if (preparationInterfaceLangList() <= 1) {
        return false;
    }
    return !!formField.formFieldConfig?.interfaceTranslation;
}

export function isCustomerTranslationEnabledOnField(formField) {
    return !!findFieldValueFromCompanyCompanyConfigurationFormField(formField?.validatedForm?.formConfig,
        formField?.formFieldConfig, "translations");

}

export function prepareTranslation(formField) {
    let translation = formField.translations;

    let userInput = cloneEntity(formField.value);
    let exceptedUserInputLang = detectInputTypingLanguage(formField);
    translation[exceptedUserInputLang] = userInput;
    return translation;
}

/**
 *
 * @param formField
 * @returns {{customerTranslationEnabled: *, requiredLangForDb: (string|*), topenTranslationDialogranslation: *, typingLanguage: string, interfaceTranslationEnabled: *}}
 */
export function prepareDialogInput(formField, openFromGrid) {
    let translation = prepareTranslation(formField);
    let typingLanguage = detectInputTypingLanguage(formField);
    let requiredLangForDb = detectDBLanguage(formField);
    let interfaceTranslationEnabled = isInterfaceTranslationEnabledOnField(formField);
    let customerTranslationEnabled = isCustomerTranslationEnabledOnField(formField);

    return {
        translation,
        typingLanguage,
        requiredLangForDb,
        interfaceTranslationEnabled,
        customerTranslationEnabled,
        formField: formField,
        fieldConfig: formField.formFieldConfig,
        openFromGrid
    }
}

export function getValueForSaveInDb(translations, formField) {
    return translations[detectDBLanguage(formField)];
}

export function getValueForTypingInput(translations, formField) {
    return translations[detectInputTypingLanguage(formField)];
}

/**
 *
 * @param validatedField
 */
export function prepareMissingTranslationsAfterLoad(validatedField) {
    return;
}


/**
 *
 * @param {FormModel} formModel
 */

export async function fillMissingTranslationsBeforeSaveToDb(formModel) {
    let dialog;

    try {
        await Promise.all(formModel._forEachValidatedField(async (name, field) => {
            if (isCustomerTranslationEnabledOnField(field) || isInterfaceTranslationEnabledOnField(field)) {
                if (field.value && (field.value instanceof String || typeof field.value === "string")) {
                    // TODO - w przyszłości dodać tłumaczenia dla innych typów o ile będą potrzebne.

                    let typingLanguage = detectInputTypingLanguage(field);

                    let languages = await prepareListLanguage(field);
                    let emptyValuesOfLanguages = [];
                    for (const key in languages) {
                        if (!field.translations.hasOwnProperty(key)) {
                            if (!dialog) {
                                dialog = DialogManager.openPendingDialog($tr("translationDispatcher.PleaseWait", "Please wait"),
                                    $tr("translationDispatcher.TranslationGenerated", "Translations are being generated."));
                            }
                            emptyValuesOfLanguages.push(key);
                        }
                    }
                    // po przejściu ma nie być typing language
                    if (emptyValuesOfLanguages.length > 0) {
                        const newAssignedTranslations = await Rpc.Regions.translateToMultiLang(typingLanguage, emptyValuesOfLanguages, field.value);
                        field.translations = {...field.translations, ...newAssignedTranslations};
                    }
                }
            }
        }));
    } finally {
        dialog?.$nextTick(() => {
            DialogManager.closePendingDialog();
        });
    }
}

export async function prepareListLanguage(field) {
    let isCustomerTranslation = isCustomerTranslationEnabledOnField(field);
    let isInterfaceTranslation = isInterfaceTranslationEnabledOnField(field);

    let languages;

    if (isCustomerTranslation && isInterfaceTranslation) {
        let regionLanguages = await preparationRegionLangList();
        let interfaceLanguages = await preparationInterfaceLangList();
        languages = {...interfaceLanguages, ...regionLanguages,};
    } else {
        if (isCustomerTranslation) {
            languages = await preparationRegionLangList();
        } else if (isInterfaceTranslation) {
            languages = await preparationInterfaceLangList();
        }
    }

    return languages;
}

export async function preparationRegionLangList() {
    const regionsConfig = await Rpc.Regions.loadRegionsConfig();

    const languagesToRegions = {};
    const regions = regionsConfig.regions;
    regions.forEach(region => {
        if (!languagesToRegions[region.baseLanguage]) {
            languagesToRegions[region.baseLanguage] = [];
        }

        languagesToRegions[region.baseLanguage].push(region);
    });
    Object.keys(languagesToRegions).forEach(key => {
        languagesToRegions[key] = "";
    });
    return languagesToRegions;
}

export async function loadInterfaceLangList() {
    let interfaceLanguages = await Rpc.AppTranslations.languageInSelectedInterfaceLanguage();
    let modifiedLanguages = {};
    Object.keys(interfaceLanguages).forEach(key => {
        modifiedLanguages[key] = "";
    });
    return modifiedLanguages;
}

export function preparationInterfaceLangList() {
    return SessionManager.interfaceLanguages;
}

export function flatTranslationsBeforeRecordSave(translations, fieldConfig) {
    let codes = [];
    if (fieldConfig instanceof DecimalFormFieldConfig) {
        if (fieldConfig.formFieldConfig.dataType === "DecimalWithCurrency") {
            for (const [key, value] of Object.entries(translations)) {
                if (!value || !key) {
                    delete translations[key];
                }
            }
        }
    }
    for (let regionCode in translations) {
        codes.push(regionCode);
    }

    for (let regionCode of codes) {
        if (!translations[regionCode]) {
            continue;
        }

        let region = SessionManager.getRegionByCode(regionCode);
        // if (!region) {
        //     console.log(region);
        // }
        if (region?.type === 'region') {
            let langCode = region.language;
            if (translations[langCode] === translations[regionCode]) {
                delete translations[regionCode];
            }
            if (!translations[langCode] && translations[regionCode] === translations['def']) {
                delete translations[regionCode];
            }
        }
    }
}

export async function showTranslationActivity(props) {
    let activity = await ActivityManager.showActivity(activityClass, props);
    return await activity.getResult();
}


export function defaultCurrencyValForRegion(regionCone) {
    let res = new DecimalWithCurrency();
    res.decimal = new BigDecimal("0");
    res.currency = SessionManager.getRegionByCode(regionCone.toLowerCase())?.currencyRef;
    return res;
}

export async function saveFieldsTranslationsFromDialog(result, formField) {
    if (!result) {
        return
    }
    let translation = result["translations"];
    for (let lang in translation) {
        formField.translations[lang] = translation[lang];
    }
    let clazz = formField.validatedForm.formConfig.formClassLong;
    let id = formField.validatedForm.originalSource.id;
    let fieldName = formField.formFieldConfig.fieldName;
    let businessRecord = await Rpc.Regions.assignTranslations(translation, fieldName, clazz, id);
    if (businessRecord) {
        formField.validatedForm.originalSource = businessRecord;
        await formField.saveField();
    }
    return translation;
}